import { useCallback } from 'react'
import { config } from '@/lib/config.runtime'
import type { AuthState } from '@/hooks/useAuth'
import type { RoutePath } from '@/hooks/useHashRoute'
import { useUserRoles } from '@/hooks/useUserRoles'
import { useSystemPrompt } from '@/hooks/useSystemPrompt'
import AccountMenu from '@/components/AccountMenu'
import Logo from '@/components/Logo'
import RoleManagement from '@/components/RoleManagement'
import SystemPromptEditor from '@/components/SystemPromptEditor'
import styles from './Admin.module.css'

export type AdminProps = {
  auth: Extract<AuthState, { kind: 'authed' }>
  /** The signed-in admin's oid, already resolved by the shell from /api/me. */
  currentOid: string
  onNavigate: (path: RoutePath) => void
}

export default function Admin({ auth, currentOid, onNavigate }: AdminProps) {
  const acquireToken = useCallback(() => auth.acquireToken(), [auth])
  const roles = useUserRoles({ baseUrl: config.apiBaseUrl, acquireToken })
  const systemPrompt = useSystemPrompt({ baseUrl: config.apiBaseUrl, acquireToken })

  const handleSavePrompt = useCallback(
    (prompt: string) => {
      void systemPrompt.save(prompt)
    },
    [systemPrompt],
  )

  const handleGrant = useCallback(
    (email: string) => {
      void roles.grantAdmin(email)
    },
    [roles],
  )
  const handleRevoke = useCallback(
    (oid: string) => {
      void roles.revokeAdmin(oid)
    },
    [roles],
  )

  return (
    <main className={styles.main}>
      <header className={styles.header}>
        <div className={styles.brand}>
          <button
            className={styles.back}
            onClick={() => onNavigate('chat')}
            aria-label="Tilbake til chat"
          >
            ←
          </button>
          <div className={styles.lockup}>
            <Logo variant="text" decorative className={styles.logoText} />
            <Logo variant="icon" className={styles.logoIcon} />
          </div>
          <span className={styles.tagline}>
            <strong>Adminpanel</strong>
          </span>
        </div>
        <AccountMenu
          username={auth.account.username}
          onLogout={() => void auth.logout()}
          currentPage="admin"
          onNavigate={onNavigate}
          isAdmin
        />
      </header>

      <div className={styles.content}>
        <RoleManagement
          state={roles.state}
          currentOid={currentOid}
          grantError={roles.grantError}
          onGrant={handleGrant}
          onRevoke={handleRevoke}
        />

        <SystemPromptEditor
          state={systemPrompt.state}
          saveState={systemPrompt.saveState}
          onSave={handleSavePrompt}
        />
      </div>
    </main>
  )
}
